export class ForbiddenError extends Error {
  constructor(message?: string) {
    super(message);
  }
}

/**
 * Thrown by a widget query fn when the API returns 403 with
 * `{ code: "SLACK_SCOPE_REQUIRED", integrationId }`. Widgets catch this to render a
 * "Reconnect Slack" affordance (calling `reconnect(integrationId)` from
 * `@/lib/reconnect`) instead of a generic error, and never retry it.
 */
export class SlackScopeError extends Error {
  constructor(
    message: string,
    public integrationId?: string,
  ) {
    super(message);
    this.name = "SlackScopeError";
  }
}

/**
 * Thrown by Google widget requests when the API returns 403 with
 * `{ code: "GOOGLE_SCOPE_REQUIRED", scopes, integrationId }`: the account is
 * connected but has not allowed what this widget needs yet. It is a
 * `ForbiddenError`, so the existing no-retry handling still applies.
 */
export class GoogleScopeError extends ForbiddenError {
  constructor(
    message: string,
    public scopes: string[] = [],
    public integrationId?: string,
  ) {
    super(message);
    this.name = "GoogleScopeError";
  }
}

/**
 * Shared replacement for the `.catch(err => { throw new Error(...) })` block that
 * was copy-pasted across ~90 query/mutation functions. Behaviour is identical:
 * surface the API's `response.data.message`, falling back to `fallback`.
 *
 * Usage: `request.get("/x").catch(normalizeRequestError("Failed to load X."))`
 *
 * Note: status-specific mapping (403 -> ForbiddenError) stays in the hooks that
 * need it, to preserve existing retry/branching behaviour.
 */
export const normalizeRequestError =
  (fallback: string) =>
  (err: unknown): never => {
    const message = (err as { response?: { data?: { message?: string } } })
      ?.response?.data?.message;
    throw new Error(message ? message : fallback);
  };
